import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import "./SmartAppBanner.css";

function SmartAppBanner() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [installedStatus, setInstalledStatus] = useState("unknown");

  const platform = useMemo(() => {
    const ua = window.navigator.userAgent;
    if (/Android/i.test(ua)) return "android";
    if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
    return "other";
  }, []);

  const currentUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
  const isShareLikePage = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const hasPostQuery = query.has("post") || query.has("postId") || query.has("id");
    const pathLooksShared = /\b(post|posts|share)\b/i.test(location.pathname);
    return hasPostQuery || pathLooksShared;
  }, [location.pathname, location.search]);

  const appSchemeLink = process.env.REACT_APP_APP_SCHEME_LINK || "instapaw://open";
  const androidPackage = process.env.REACT_APP_ANDROID_PACKAGE || "com.santoshbhatt.instapaw";
  const androidStoreUrl = process.env.REACT_APP_ANDROID_STORE_URL || `https://play.google.com/store/apps/details?id=${androidPackage}`;
  const iosAppStoreUrl = process.env.REACT_APP_IOS_STORE_URL || "https://apps.apple.com/in/app/instapaw/id6764773428";

  useEffect(() => {
    const detectInstalledApp = async () => {
      if (platform !== "android") {
        setInstalledStatus("unknown");
        return;
      }

      if (typeof window.navigator.getInstalledRelatedApps !== "function") {
        setInstalledStatus("unknown");
        return;
      }

      try {
        const apps = await window.navigator.getInstalledRelatedApps();
        const isInstalled = apps.some((app) => {
          return app.id === androidPackage || app.packageName === androidPackage;
        });
        setInstalledStatus(isInstalled ? "installed" : "not_installed");
      } catch (error) {
        setInstalledStatus("unknown");
      }
    };

    detectInstalledApp();
  }, [androidPackage, platform]);

  const ctaMessage = useMemo(() => {
    if (installedStatus === "installed") {
      return "Open this shared post directly in Instapaw.";
    }

    if (installedStatus === "not_installed") {
      return platform === "ios"
        ? "Instapaw app is not installed. Install it from the App Store."
        : "Instapaw app is not installed. Install it from Play Store.";
    }

    return platform === "ios"
      ? "Open in Instapaw, or install from the App Store if needed."
      : "Open in Instapaw, or install from Play Store if needed.";
  }, [installedStatus, platform]);

  const shouldShow = (platform === "android" || platform === "ios") && isShareLikePage && !dismissed;

  if (!shouldShow) {
    return null;
  }

  const dismissBanner = () => {
    setDismissed(true);
  };

  const openApp = () => {
    if (platform === "android") {
      const fallback = encodeURIComponent(currentUrl);
      const intentTarget = `${window.location.host}${location.pathname}${location.search}${location.hash}`;
      const intentUrl = `intent://${intentTarget}#Intent;scheme=https;package=${androidPackage};S.browser_fallback_url=${fallback};end`;

      let didHide = false;
      const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
          didHide = true;
        }
      };

      document.addEventListener("visibilitychange", onVisibilityChange, { once: true });
      window.location.href = intentUrl;

      window.setTimeout(() => {
        if (!didHide) {
          setInstalledStatus("not_installed");
        }
      }, 1300);

      return;
    }

    if (platform === "ios") {
      const deepLink = `${appSchemeLink}?url=${encodeURIComponent(currentUrl)}`;
      const start = Date.now();

      window.location.href = deepLink;
      window.setTimeout(() => {
        if (Date.now() - start < 1500) {
          window.location.href = iosAppStoreUrl;
        }
      }, 1200);
    }
  };

  const installApp = () => {
    if (platform === "android") {
      window.location.href = androidStoreUrl;
      return;
    }

    if (platform === "ios") {
      window.location.href = iosAppStoreUrl;
    }
  };

  return (
    <aside className="smart-cta" role="region" aria-label="Open Instapaw app">
      <p className="smart-cta__text">{ctaMessage}</p>
      <div className="smart-cta__actions">
        <button
          className="smart-cta__btn smart-cta__btn--primary"
          onClick={installedStatus === "not_installed" ? installApp : openApp}
        >
          {installedStatus === "not_installed" ? "Install App" : "Open App"}
        </button>
        <button
          className="smart-cta__btn smart-cta__btn--secondary"
          onClick={installedStatus === "not_installed" ? openApp : installApp}
        >
          {installedStatus === "not_installed" ? "Try Open App" : "Install App"}
        </button>
        <button className="smart-cta__btn smart-cta__btn--ghost" onClick={dismissBanner}>
          Not now
        </button>
      </div>
    </aside>
  );
}

export default SmartAppBanner;
